import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { orderApi } from "@/services/api";
import { OrderPayment, PaymentStatus } from "@/models";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, CircleDollarSign, CreditCard } from "lucide-react";

interface PaymentsTabProps {
  orderId: number;
}

const getBadgeVariant = (status: PaymentStatus) => {
  switch (status) {
    case PaymentStatus.paid:
      return "success";
    case PaymentStatus.processing:
      return "warning";
    case PaymentStatus.pending:
      return "secondary";
    case PaymentStatus.failed:
    case PaymentStatus.cancelled:
      return "destructive";
    default:
      return "outline";
  }
};

const getPaymentStatusText = (status: PaymentStatus) => {
  switch (status) {
    case PaymentStatus.paid:
      return "Completed";
    case PaymentStatus.processing:
      return "Processing";
    case PaymentStatus.pending:
      return "Pending";
    case PaymentStatus.failed:
      return "Failed";
    case PaymentStatus.cancelled:
      return "Cancelled";
    case PaymentStatus.refunded:
      return "Refunded";
    default:
      return status;
  }
};

const getPaymentMethodIcon = (method: string) => {
  switch (method) {
    case "Credit Card":
    case "Debit Card":
    case "Stripe":
      return <CreditCard className="h-4 w-4" />;
    default:
      return <CircleDollarSign className="h-4 w-4" />;
  }
};

export const PaymentsTab = ({ orderId }: PaymentsTabProps) => {
  const { data: payments, isLoading, error } = useQuery({
    queryKey: ["order-payments", orderId],
    queryFn: async () => {
      console.log(`Fetching payments for order ${orderId}`);
      const result = await orderApi.getOrderPayments(orderId);
      console.log('Payments result:', result);
      return result;
    },
    enabled: !!orderId,
    retry: 1, // Only retry once to avoid excessive retries if the endpoint is not working
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
      </div>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
          <CardDescription>
            Error loading payment data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">
            {error instanceof Error ? error.message : "An unknown error occurred"}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!payments || !Array.isArray(payments) || payments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
          <CardDescription>
            No payment records found for this order
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payments</CardTitle>
        <CardDescription>
          Payment history for this order
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>
                  {new Date(payment.created_at).toLocaleDateString()}
                  <div className="text-xs text-muted-foreground">
                    {new Date(payment.created_at).toLocaleTimeString()}
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  ${Number(payment.amount).toFixed(2)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getPaymentMethodIcon(payment.payment_method)}
                    <span>{payment.payment_method}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-xs font-mono">
                    {payment.payment_reference ? 
                      payment.payment_reference.substring(0, 12) + "..." : 
                      "N/A"}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={getBadgeVariant(payment.status)}>
                    {getPaymentStatusText(payment.status)}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
